import type {ReactElement} from 'react';
import React from 'react';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {TranscriptShadowOptionProps} from './transcript-shadow-option.types';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {useGetShadowOptionsObjectForActiveTranscriptItem} from '@Hooks/poster-editor/useGetShadowOptionsObjectForActiveTranscriptItem';
import {
  convertShadowOptionsBlurToFabricShadowBlur,
  getDefaultFabricShadowForType,
  getFabricShadowOrGlowTypeFromShadowType,
  isCustomShadow,
  isShadow,
} from '@Utils/fabric-shadow.util';
import {getFabricShadowOffsetsFromShadowOptions} from '@Utils/fabric-shadow.util';
import type {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {BaseItemShadowOptions} from '@Components/poster-editor/components/base-item-shadow-options/base-item-shadow-options';

export function TranscriptShadowOption({...props}: TranscriptShadowOptionProps): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptShadowOption');
  }

  const activeTranscriptItemShadowOptions = useGetShadowOptionsObjectForActiveTranscriptItem();
  if (activeTranscriptItemShadowOptions === undefined) {
    throw new Error('Transcript item subtitles do not have a shadow property');
  }

  const onToggleShadow = async (newShadowType: ShadowType | null): Promise<void> => {
    const newType = getFabricShadowOrGlowTypeFromShadowType(newShadowType);
    const currentType = activeTranscriptItemShadowOptions?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeItem.gitype, activeItem.getScaleForShadow());

    if (!updatedShadow) {
      await activeItem.updateAllSubtitlesStylesFromSubtitleObject({shadow: null});

      return;
    }

    const shouldPreserveOffsets = isShadow(currentType) && isShadow(newType) && !isCustomShadow(currentType) && !isCustomShadow(newType);

    const {offsetX, offsetY, ...restShadow} = updatedShadow;

    const shadowToApply = shouldPreserveOffsets ? {...restShadow} : updatedShadow;

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject({shadow: shadowToApply});
  };

  const updateOpacity = async (dropShadowAlpha: number, undoable = true): Promise<void> => {
    if (!activeTranscriptItemShadowOptions) {
      return;
    }

    const dropShadowColor = activeTranscriptItemShadowOptions.color.slice() as unknown as RGBA;
    dropShadowColor[3] = dropShadowAlpha;

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        shadow: {
          color: rgbaToHexString(dropShadowColor),
        },
      },
      {undoable}
    );
  };

  const updateAngle = async (dropShadowAngle: number, undoable = true): Promise<void> => {
    if (!activeTranscriptItemShadowOptions) {
      return;
    }

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeTranscriptItemShadowOptions, angle: dropShadowAngle}, activeItem.getScaleForShadow()),
        },
      },
      {undoable}
    );
  };

  const updateDistance = async (dropShadowDistance: number, angle: number, undoable = true): Promise<void> => {
    if (!activeTranscriptItemShadowOptions) {
      return;
    }

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeTranscriptItemShadowOptions, distance: dropShadowDistance, angle}, activeItem.getScaleForShadow()),
        },
      },
      {undoable}
    );
  };

  const updateBlur = async (dropShadowBlur: number, undoable = true): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        shadow: {
          blur: convertShadowOptionsBlurToFabricShadowBlur(dropShadowBlur, activeItem.getScaleForShadow()),
        },
      },
      {undoable}
    );
  };

  return (
    <BaseItemShadowOptions
      shadowOptions={activeTranscriptItemShadowOptions}
      itemType={activeItem.gitype}
      onClickColorFill={(): void => {
        props.onTranscriptShadowColorFillClick();
      }}
      onToggleShadow={onToggleShadow}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
    />
  );
}
